import PropTypes from 'prop-types'
import { TextField, InputAdornment } from '@mui/material'
import { useFieldState } from 'hooks/useFieldState'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useFieldFocus } from 'context/FieldFocusContext'
import { useEffect, useRef } from 'react'

const MuiEmailField = ({
  id,
  showAdornment = false,
  value,
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur = () => {},
  enableHelpText,
  helpText,
  disabled,
  required,
  defaultErrorState,
  forceError,
  ...rest
}) => {
  const validateEmail = (val) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
  }

  const { isError, isSuccess, handleOnblur } = useFieldState({
    required,
    defaultErrorState,
    validatorFunction: validateEmail,
    forceError,
  })

  const handleOnblurWithValidation = (e) => {
    handleOnblur?.(value)
    handleBlur()

    if (validateEmail(value)) {
      onBlur?.(e)
    }
  }

  // const handleBlur = () => {
  //   handleOnblur(value)
  //   if (validateEmail(value)) {
  //     onBlur?.(value)
  //   }
  // }

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  const styles = {
    '& .MuiInputLabel-asterisk': {
      color: '#ea0606',
    },
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': {
      color: '#ea0606',
    },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
  }

  return (
    <TextField
      disabled={disabled}
      required={required}
      id={id}
      label={label}
      value={value}
      inputRef={inputRef}
      //   placeholder={placeholder}
      onChange={onChange}
      onFocus={(e) => {
        handleFocus(fieldId, e.target)
        onFocus?.(e)
      }}
      onBlur={handleOnblurWithValidation}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      error={isError}
      fullWidth
      type="email"
      placeholder="johnsmith@gmail.com"
      inputProps={{
        inputMode: 'email',
        autoComplete: 'email',
      }}
      sx={styles}
      InputProps={{
        endAdornment: (
          <>
            {showAdornment && (
              <InputAdornment position="end">
                {isError ? (
                  <ErrorOutline
                    color="error"
                    sx={{ fontSize: '24px !important', marginRight: '8px' }}
                  />
                ) : isSuccess ? (
                  <Check
                    color="success"
                    sx={{ fontSize: '24px !important', marginRight: '8px' }}
                  />
                ) : null}
              </InputAdornment>
            )}
          </>
        ),
      }}
      {...rest}
    />
  )
}

MuiEmailField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  defaultErrorState: PropTypes.bool,
  forceError: PropTypes.bool,
}

export default MuiEmailField
